/* eslint-disable no-restricted-globals */
/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print"; // Ensure this import is present
import axios from "axios";
import { Alert, Box, IconButton, Paper, Stack, Typography, useTheme } from "@mui/material";
import BugReportIcon from "@mui/icons-material/BugReport";
import { getTypedVal } from "utils/urls";
import {
  IRoadLayoutConfig,
  ILuminaireLayoutConfig,
  ILightingClassConfig,
  IOptimizationConfig,
  IRoadwayConfig,
  IFamilyConfig,
  IProjectConfig,
  ISdFpath,
  PortfolioData,
} from "types";
import { createRequestJSON, downloadPdf } from "utils";
import { generatePdfFileName } from "utils/helpers";
import { APP_COMPANY_NAME } from "consts";
import { usePrintMode } from "hooks/is-print";
import useBreakPages from "hooks/page-breaks";
import useGeneratePdf from "hooks/use-generate-pdf";
import { Loader } from "components/Loader";
import Outprint from "./components/outprint/Outprint";
import { createPDFLink } from "./utils/download-links";
import { getSidewalkPlacing } from "./utils/sidewalk";
import { getFamilyImage } from "./utils/result-data";
import { buildResultViewModel } from "./utils/result-mapper";
import type { SurfaceWithIlluminance } from "./types";
import { useResultFetch } from "./hooks/useResultFetch";
import LuminanceTable from "./components/tables/LuminanceTable";
import IlluminanceTable from "./components/tables/IlluminanceTable";
import IlluminanceBordersTable from "./components/tables/IlluminanceBordersTable";
import LightingCalculation from "./components/LightingCalculation";
import RoadwayInfo from "./components/RoadwayInfo";
import LuminaireRow from "./components/LuminaireRow";
import LuminaireFound from "./components/LuminaireFound";
import DownloadLinks from "./components/DownloadLinks";
import HeatmapDebugModal from "./components/HeatmapDebugModal";
import { printCarbonePdf } from "./utils/carbone-print";
import getStyles from "./styles";

interface IProps {
  families: { data: PortfolioData | null };
  familyConfig: IFamilyConfig;
  selectedResult: string | number;
  roadLayoutConfig: IRoadLayoutConfig;
  roadwayConfig: IRoadwayConfig;
  luminaireLayoutConfig: ILuminaireLayoutConfig;
  lightingClassConfig: ILightingClassConfig;
  optimizationConfig: IOptimizationConfig;
  projectConfig: IProjectConfig;
  resultFamilyIndex: number;
  updateProject: (key: string, value: string) => void;
}

const Result: FC<IProps> = ({
  families,
  familyConfig,
  selectedResult,
  roadLayoutConfig,
  roadwayConfig,
  lightingClassConfig,
  luminaireLayoutConfig,
  optimizationConfig,
  projectConfig,
  resultFamilyIndex,
  updateProject,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const appLang = i18n.resolvedLanguage;
  const isPrintMode = usePrintMode();
  const portfolioId = getTypedVal("portfolio_id");
  const familyIds = getTypedVal("family_ids");
  const resultPageRef = useRef<HTMLDivElement | null>(null);
  const { pdf, loading: pdfLoading, error: pdfError, generatePdf } = useGeneratePdf();
  const [debugModalOpen, setDebugModalOpen] = useState(false);

  const { data, error } = useResultFetch({
    portfolioId,
    familyConfig,
    roadLayoutConfig,
    roadwayConfig,
    lightingClassConfig,
    luminaireLayoutConfig,
    optimizationConfig,
    appLang,
  });

  const pdfFileName = generatePdfFileName(projectConfig, t("Calculation Results"));
  const pageHeight = 1122; // A4 height at 96 DPI (adjust based on your needs)
  const margin = 50; // Margin for headers, footers, or padding

  const { prepareForPrint } = useBreakPages({
    containerRef: resultPageRef,
    pageHeight,
    margin,
  });

  const {
    request: {
      params: { scene },
    },
  } = createRequestJSON(portfolioId, familyConfig, roadLayoutConfig, roadwayConfig, lightingClassConfig, luminaireLayoutConfig, optimizationConfig);

  const resolvedFamilies = ("families" in families ? families : families?.data) ?? {};
  const family_image = getFamilyImage(resolvedFamilies as Parameters<typeof getFamilyImage>[0], familyIds, resultFamilyIndex);
  const { maintenanceFactor } = optimizationConfig;
  const sidewalk_placing = getSidewalkPlacing(scene.surface.surfaces);

  const resultVM = data !== null && selectedResult !== "" ? buildResultViewModel(data, selectedResult, scene.lum_row?.style, lightingClassConfig) : null;

  const {
    articleNumber: article_number = "N/A",
    varNumber: var_number,
    delta = "N/A",
    lph = "N/A",
    overhang = "N/A",
    tilt = "N/A",
    lvkPngUrl: lvk_png_url = null,
    lvkImgUrl: lvk_img_url = null,
    pdfUrl: pdf_url = "",
    lampset = "",
    lightFlux: light_flux = "N/A",
    power = null,
    nLums: n_lums = null,
    loadperkm = "N/A",
    recadzLink = "",
    ldtLink = "",
    requestHash: request_hash = "",
    ulor = -1,
    fTi: f_ti = -1,
    article,
    luminance = null,
    luminanceSummary: luminance_summary = null,
    illuminance = null,
    displayIlluminance = false,
    displayLuminance = false,
    surfaces = null,
    subVal = [],
    subValResult,
    sState: s_state = null,
    eStateLeft: e_state_left = "",
    eStateRight: e_state_right = "",
  } = (resultVM ?? {}) as NonNullable<typeof resultVM>;

  const pos = luminaireLayoutConfig.luminaireConfig.toLowerCase();

  const reactToPrintFn = useReactToPrint({
    contentRef: resultPageRef,
    documentTitle: pdfFileName,
    onAfterPrint: () => {
      prepareForPrint();
      const url = createPDFLink(`/api/events/${request_hash}/track/?event=download_pdf`, appLang);
      axios.get(url).catch((error) => console.log(error));
    },
    onBeforePrint: async () => {
      prepareForPrint();
    },
  });

  const handleCarbone = async () => {
    await printCarbonePdf({
      roadLayoutConfig,
      family_image,
      luminance,
      lightingClassConfig,
      selectedResult,
      projectConfig,
      data,
      article,
      displayLuminance,
      displayIlluminance,
      appLang,
      luminance_summary,
      illuminance,
      surfaces,
      sidewalk_placing,
      subVal,
      subValResult,
      loadperkm: typeof loadperkm === "number" ? loadperkm : null,
      lampset,
      lvk_png_url: lvk_png_url ?? undefined,
      lvk_img_url: lvk_img_url ?? undefined,
      article_number,
      var_number: var_number ?? "",
      light_flux: typeof light_flux === "number" ? light_flux : null,
      power: typeof power === "number" ? power : null,
      delta: typeof delta === "number" ? delta : null,
      s_state,
      e_state_left,
      e_state_right,
      lph: typeof lph === "number" ? lph : null,
      overhang: typeof overhang === "number" ? overhang : null,
      tilt: typeof tilt === "number" ? tilt : null,
      maintenanceFactor: typeof maintenanceFactor === "number" ? maintenanceFactor : null,
      roadwayConfig,
      resultPageRef,
      ulor: typeof ulor === "number" ? ulor.toString() : undefined,
      n_lums: typeof n_lums === "number" ? n_lums : undefined,
      f_ti: typeof f_ti === "number" ? f_ti : undefined,
      pdfFileName,
      generatePdf,
      t,
    });
  };

  // Auto-download PDF when it's generated
  useEffect(() => {
    if (pdf) {
      downloadPdf(pdf, pdfFileName);
    }
  }, [pdf, pdfFileName]);

  // exit early when having an error
  if (error.message !== null) {
    return (
      <Box p="calc(2 * var(--rhythm))">
        <Alert variant="outlined" severity="error" icon={false}>
          <Typography variant="body2">
            Error message: <pre>{error.message}</pre>
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (data !== null && selectedResult !== "" && resultVM === null) {
    return (
      <Box p="calc(2 * var(--rhythm))">
        <Typography variant="h3" mb="var(--rhythm)">
          No result found
        </Typography>
        <Typography variant="body2" component="p">
          Please select a valid result from the previous step.
        </Typography>
      </Box>
    );
  }

  if (pdfError) {
    console.error("Carbone PDF generation error:", pdfError);
  }

  return (
    <>
      {selectedResult !== "" && (
        <Stack sx={styles.container}>
          <LightingCalculation
            projectConfig={projectConfig}
            isPrintMode={isPrintMode}
            updateProject={updateProject}
            showButtons={!!(data && resultVM !== null)}
            pdfUrl={pdf_url}
            ldtUrl={ldtLink}
            recadzUrl={recadzLink}
            requestHash={request_hash}
            reactToPrint={reactToPrintFn}
            carbonePrint={handleCarbone}
          />

          {!(data && resultVM !== null) && <Loader />}

          {data && resultVM !== null && (
            <>
              <RoadwayInfo roadwayConfig={roadwayConfig} roadLayoutConfig={roadLayoutConfig} luminaireLayoutConfig={luminaireLayoutConfig} />

              <LuminaireRow
                roadwayConfig={roadwayConfig}
                delta={delta}
                position={pos}
                numberOfLuminaires={n_lums}
                lph={lph}
                overhang={overhang}
                tilt={tilt}
                maintenanceFactor={maintenanceFactor}
              />

              <LuminaireFound
                families={families}
                imgLvk={lvk_img_url}
                articleNumber={article_number}
                varNumber={var_number}
                lightFlux={light_flux}
                power={power}
                loadPerKm={loadperkm}
                lampset={lampset}
                selectedResult={selectedResult}
                resultFamilyIndex={resultFamilyIndex}
              />

              {/* luminance table and heatmaps */}
              {displayLuminance &&
                luminance !== null &&
                Object.keys(luminance).map((key: string, index: number) => (
                  <LuminanceTable
                    key={key}
                    lightingClassConfig={lightingClassConfig}
                    luminance={luminance[key]}
                    luminance_summary={luminance_summary}
                    illuminance={illuminance}
                    surfaces={surfaces}
                    sidewalk_placing={sidewalk_placing}
                    divId={`heatmaproot${key}`}
                    displayHeader={key === "0"}
                    ulor={ulor}
                    e_state_left={e_state_left}
                    s_state={s_state ?? ""}
                    e_state_right={e_state_right}
                    roadLayoutConfig={roadLayoutConfig}
                    index={index}
                    showLane={Object.keys(luminance).length > 1}
                  />
                ))}

              {/* illuminance table and heatmaps */}
              {displayIlluminance && illuminance !== null && surfaces !== null && (
                // biome-ignore lint/complexity/noUselessFragments: <explanation>
                <IlluminanceTable
                  lightingClassConfig={lightingClassConfig}
                  illuminance={illuminance}
                  surfaces={surfaces as SurfaceWithIlluminance[]}
                  sidewalk_placing={sidewalk_placing}
                  subVal={subVal as unknown as ISdFpath[]}
                  subValResult={subValResult ?? []}
                  ulor={ulor}
                  f_ti={f_ti}
                  s_state={s_state}
                  e_state_left={e_state_left}
                  e_state_right={e_state_right}
                  roadLayoutConfig={roadLayoutConfig}
                />
              )}
            </>
          )}

          {displayLuminance && sidewalk_placing !== null && illuminance !== null && surfaces !== null && (
            // biome-ignore lint/complexity/noUselessFragments: <explanation>
            <IlluminanceBordersTable
              lightingClassConfig={lightingClassConfig}
              illuminance={illuminance}
              surfaces={surfaces as SurfaceWithIlluminance[]}
              sidewalk_placing={sidewalk_placing}
              subVal={subVal as unknown as ISdFpath[]}
              subValResult={subValResult ?? []}
              ulor={ulor}
              f_ti={f_ti}
            />
          )}

          {data && resultVM !== null && (
            <Stack component={Paper} sx={styles.download}>
              <DownloadLinks
                id="downloads_bottom"
                pdfUrl={pdf_url}
                ldtUrl={ldtLink}
                recadzUrl={recadzLink}
                requestHash={request_hash}
                updateProject={updateProject}
                reactToPrint={reactToPrintFn}
                carbonePrint={handleCarbone}
              />
              {APP_COMPANY_NAME.toLowerCase() === "relux" && (
                <IconButton
                  color="primary"
                  onClick={() => setDebugModalOpen(true)}
                  sx={{
                    width: "calc(2.5 * var(--rhythm))",
                    height: "calc(2.5 * var(--rhythm))",
                    ml: "var(--rhythm)",
                  }}
                >
                  <BugReportIcon />
                </IconButton>
              )}
            </Stack>
          )}
        </Stack>
      )}

      <Outprint
        data={data}
        article={article}
        var_number={var_number}
        delta={delta}
        lph={lph}
        overhang={overhang}
        tilt={tilt}
        lvk_png_url={lvk_png_url ?? ""}
        lvk_img_url={lvk_img_url ?? ""}
        article_number={article_number}
        lampset={lampset}
        maintenanceFactor={maintenanceFactor}
        luminance={luminance}
        luminance_summary={luminance_summary}
        illuminance={illuminance}
        surfaces={surfaces}
        light_flux={light_flux}
        subVal={subVal}
        subValResult={subValResult}
        roadLayoutConfig={roadLayoutConfig}
        lightingClassConfig={lightingClassConfig}
        projectConfig={projectConfig}
        project={projectConfig}
        e_state_left={e_state_left}
        e_state_right={e_state_right}
        displayIlluminance={displayIlluminance}
        displayLuminance={displayLuminance}
        family_image={family_image}
        loadperkm={loadperkm?.toString()}
        power={power?.toString() ?? ""}
        pos={pos}
        resultPageRef={resultPageRef}
        roadwayConfig={roadwayConfig}
        sidewalk_placing={sidewalk_placing}
        s_state={s_state ?? ""}
        selectedResult={selectedResult?.toString()}
        ulor={ulor}
        n_lums={n_lums ?? 0}
        f_ti={f_ti}
      />

      {/* Debug modal for testing heatmap capture */}
      <HeatmapDebugModal
        open={debugModalOpen}
        onClose={() => setDebugModalOpen(false)}
        luminanceKeys={luminance ? Object.keys(luminance) : []}
        hasBorderLeft={roadLayoutConfig?.borderAreaLeft || false}
        hasBorderRight={roadLayoutConfig?.borderAreaRight || false}
      />
    </>
  );
};

export default Result;
